import React, { useEffect, useState } from 'react';
import {
  Building2, FolderKanban, Inbox, Radar, FileText, ShieldCheck, Target, Crosshair,
  Download, Loader2, GitBranch, Users, UserPlus, Activity, Zap, Eye, LogIn,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { KpiTile } from '@/components/common/KpiTile';
import { Loader, EmptyState } from '@/components/common/Feedback';
import api from '@/lib/api';
import { fmtPct, fmtDate, fmtRelative, SOURCE_LABEL, ROLE_LABEL } from '@/lib/format';
import { toast } from 'sonner';

const PROVIDER_META = {
  email: { label: 'Email', cls: 'bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200' },
  google: { label: 'Google', cls: 'bg-brand-50 text-brand-600 ring-1 ring-inset ring-brand-100' },
  microsoft: { label: 'Microsoft', cls: 'bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-200' },
};

const ProviderBadge = ({ provider }) => {
  const m = PROVIDER_META[provider] || PROVIDER_META.email;
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.cls}`}>{m.label}</span>;
};

export default function FounderDashboard() {
  const [d, setD] = useState(null);
  const [eng, setEng] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/founder/overview').then((r) => setD(r.data)).catch(() => {}),
      api.get('/founder/engagement').then((r) => setEng(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const download = async (companyId, name, anonymize) => {
    setDl(companyId + anonymize);
    try {
      const r = await api.post(`/founder/pilot-report/${companyId}?anonymize=${anonymize}`, null, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${anonymize ? 'pilot-anon' : 'pilot-' + name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not generate report'); }
    finally { setDl(null); }
  };

  if (loading) return <Loader label="Loading founder analytics..." />;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 font-display">Founder Analytics</h2>
        <p className="text-sm text-slate-500">Growth, engagement and AI validation evidence across all participating contractors.</p>
      </div>

      <Tabs defaultValue="growth" className="space-y-5">
        <TabsList data-testid="founder-tabs">
          <TabsTrigger value="growth" data-testid="tab-growth"><Activity className="h-4 w-4 mr-1.5" />Growth &amp; Engagement</TabsTrigger>
          <TabsTrigger value="validation" data-testid="tab-validation"><ShieldCheck className="h-4 w-4 mr-1.5" />AI Validation</TabsTrigger>
        </TabsList>

        {/* ---------------- Growth & Engagement ---------------- */}
        <TabsContent value="growth" className="space-y-6">
          {!eng ? (
            <EmptyState icon={Activity} title="Engagement data unavailable" description="Once customers sign in and use the product, growth metrics appear here." />
          ) : (
            <GrowthTab eng={eng} />
          )}
        </TabsContent>

        {/* ---------------- AI Validation (existing) ---------------- */}
        <TabsContent value="validation" className="space-y-6">
          {!d ? (
            <EmptyState icon={ShieldCheck} title="No validation data" description="Validation evidence appears as contractors provide feedback." />
          ) : (
            <ValidationTab d={d} dl={dl} download={download} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const GrowthTab = ({ eng }) => {
  const a = eng.active || {}; const t = eng.totals || {};
  return (
    <>
      {/* Active-user + growth tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
        <KpiTile testid="g-dau" label="Active today (DAU)" value={a.dau ?? 0} isMoney={false} variant="recovered" icon={Activity} />
        <KpiTile testid="g-wau" label="Active this week (WAU)" value={a.wau ?? 0} isMoney={false} variant="info" icon={Activity} />
        <KpiTile testid="g-mau" label="Active this month (MAU)" value={a.mau ?? 0} isMoney={false} variant="info" icon={Activity} />
        <KpiTile testid="g-users" label="Total users" value={t.users ?? 0} isMoney={false} variant="neutral" icon={Users} />
        <KpiTile testid="g-signups" label="New signups (30d)" value={t.signups_30d ?? 0} isMoney={false} variant="recovered" icon={UserPlus} meta={`${t.signups_7d ?? 0} in last 7d`} />
        <KpiTile testid="g-logins" label="Total logins" value={t.logins ?? 0} isMoney={false} variant="neutral" icon={LogIn} />
      </div>

      {/* Auth provider mix */}
      {t.by_provider && Object.keys(t.by_provider).length > 0 && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="provider-mix">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Signup method:</span>
          {Object.entries(t.by_provider).map(([p, n]) => (
            <span key={p} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <ProviderBadge provider={p} /> <span className="font-mono font-semibold text-slate-800">{n}</span>
            </span>
          ))}
        </div>
      )}

      {/* Signups feed */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800 flex items-center gap-2"><UserPlus className="h-4 w-4" />Signups feed</div>
        {eng.signups?.length === 0 ? (
          <EmptyState icon={UserPlus} title="No signups yet" description="New users appear here the moment they register." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFBFC] text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">User</th>
                  <th className="text-left">Company</th>
                  <th className="text-left">Role</th>
                  <th className="text-center">Method</th>
                  <th className="text-left">Signed up</th>
                  <th className="text-left">Last login</th>
                  <th className="text-center px-4">Logins</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {eng.signups.map((u) => (
                  <tr key={u.id} data-testid={`signup-${u.id}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{u.name || '—'}</div>
                      <div className="text-[11px] text-slate-500">{u.email}</div>
                    </td>
                    <td className="text-slate-600">{u.company_name || '—'}</td>
                    <td className="text-slate-600">{ROLE_LABEL[u.role] || u.role}</td>
                    <td className="text-center"><ProviderBadge provider={u.auth_provider} /></td>
                    <td className="text-slate-600">{u.created_at ? fmtDate(u.created_at) : '—'}</td>
                    <td className="text-slate-600">{u.last_login_at ? fmtRelative(u.last_login_at) : <span className="text-slate-400">Never</span>}</td>
                    <td className="text-center font-mono font-semibold text-slate-800 px-4">{u.login_count ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Company activity */}
        <Card className="p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800 flex items-center gap-2"><Building2 className="h-4 w-4" />Company activity</div>
          {eng.company_activity?.length === 0 ? (
            <div className="px-5 py-8 text-center text-xs text-slate-400">No company activity yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FAFBFC] text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr><th className="text-left px-4 py-2.5">Company</th><th className="text-center">Users</th><th className="text-center">Logins</th><th className="text-center">Actions 30d</th><th className="text-left px-4">Last active</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {eng.company_activity.map((c) => (
                  <tr key={c.company_id} data-testid={`company-activity-${c.company_id}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-800">{c.company_name || '—'}</td>
                    <td className="text-center text-slate-600">{c.users}</td>
                    <td className="text-center text-slate-600">{c.logins}</td>
                    <td className="text-center text-slate-600">{c.events_30d}</td>
                    <td className="px-4 text-slate-600">{c.last_active_at ? fmtRelative(c.last_active_at) : <span className="text-slate-400">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Feature usage + top pages */}
        <Card className="p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3"><Zap className="h-4 w-4" />Feature usage</div>
          {eng.features?.length ? eng.features.map((f) => (
            <div key={f.event} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0 text-sm" data-testid={`feature-${f.event}`}>
              <span className="text-slate-700">{f.label}</span>
              <span className="text-slate-600">
                <span className="font-mono font-semibold text-slate-800">{f.count}</span>
                <span className="text-slate-400"> · {f.users} {f.users === 1 ? 'user' : 'users'}</span>
              </span>
            </div>
          )) : <div className="text-xs text-slate-400">No feature usage recorded yet.</div>}

          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mt-5 mb-3"><Eye className="h-4 w-4" />Most visited pages</div>
          {eng.top_pages?.length ? eng.top_pages.slice(0, 8).map((p) => (
            <div key={p.path} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0 text-sm" data-testid={`page-${p.path}`}>
              <span className="font-mono text-slate-700 truncate max-w-[70%]">{p.path}</span>
              <span className="font-mono font-semibold text-slate-800">{p.count}</span>
            </div>
          )) : <div className="text-xs text-slate-400">No page views recorded yet.</div>}
        </Card>
      </div>
    </>
  );
};

const ValidationTab = ({ d, dl, download }) => {
  const t = d.totals; const m = d.metrics;
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
        <KpiTile testid="f-companies" label="Companies" value={t.companies} isMoney={false} variant="info" icon={Building2} />
        <KpiTile testid="f-projects" label="Projects" value={t.projects} isMoney={false} variant="neutral" icon={FolderKanban} />
        <KpiTile testid="f-events" label="Events" value={t.events} isMoney={false} variant="neutral" icon={Inbox} />
        <KpiTile testid="f-detections" label="Detections" value={t.detections} isMoney={false} variant="at_risk" icon={Radar} />
        <KpiTile testid="f-cos" label="Change orders" value={t.change_orders} isMoney={false} variant="recovered" icon={FileText} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <KpiTile testid="f-agreement" label="Agreement rate" value={fmtPct(m.agreement_rate)} isMoney={false} variant="recovered" icon={ShieldCheck} meta={`${m.total_judged} judged`} />
        <KpiTile testid="f-precision" label="Precision" value={fmtPct(m.precision)} isMoney={false} variant="info" icon={Target} meta={`${m.fp} false positives`} />
        <KpiTile testid="f-recall" label="Recall" value={fmtPct(m.recall)} isMoney={false} variant="info" icon={Crosshair} meta={`${m.fn} false negatives`} />
        <KpiTile testid="f-confidence" label="Avg confidence" value={fmtPct(m.avg_confidence)} isMoney={false} variant="neutral" meta={`Would-submit ${fmtPct(m.would_submit_rate)}`} />
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-800">Contractors</div>
        {d.companies?.length === 0 ? (
          <EmptyState icon={Building2} title="No contractor pilots yet" description="When contractors register and provide feedback, they appear here automatically." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFBFC] text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr><th className="text-left px-4 py-2.5">Company</th><th className="text-center">Projects</th><th className="text-center">Events</th><th className="text-center">Detections</th><th className="text-center">Judged</th><th className="text-center">Agreement</th><th className="text-center">Precision</th><th className="text-right px-4">Report</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {d.companies.map((c) => (
                  <tr key={c.company_id} data-testid={`founder-company-${c.company_id}`}>
                    <td className="px-4 py-3 font-medium text-slate-800">{c.name}</td>
                    <td className="text-center text-slate-600">{c.projects}</td>
                    <td className="text-center text-slate-600">{c.events}</td>
                    <td className="text-center text-slate-600">{c.detections}</td>
                    <td className="text-center text-slate-600">{c.judged}</td>
                    <td className="text-center font-mono font-semibold text-money-700">{fmtPct(c.agreement_rate)}</td>
                    <td className="text-center font-mono font-semibold text-brand-600">{fmtPct(c.precision)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={dl === c.company_id + 'true'} onClick={() => download(c.company_id, c.name, 'true')} data-testid={`report-anon-${c.company_id}`}>
                          {dl === c.company_id + 'true' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}Anon
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={dl === c.company_id + 'false'} onClick={() => download(c.company_id, c.name, 'false')}>
                          {dl === c.company_id + 'false' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}Full
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card className="p-5">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-800 mb-3"><GitBranch className="h-4 w-4" />Prompt version performance</div>
          {d.by_prompt_version?.length ? d.by_prompt_version.map((p) => (
            <div key={p.prompt_version} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0 text-sm">
              <span className="font-mono text-slate-700">{p.prompt_version} <span className="text-slate-400">({p.judged})</span></span>
              <span className="text-slate-600">Agree <span className="font-mono font-semibold text-slate-800">{fmtPct(p.agreement_rate)}</span> &middot; P <span className="font-mono">{fmtPct(p.precision)}</span> &middot; R <span className="font-mono">{fmtPct(p.recall)}</span></span>
            </div>
          )) : <div className="text-xs text-slate-400">No data yet.</div>}
        </Card>
        <Card className="p-5">
          <div className="text-sm font-semibold text-slate-800 mb-3">Highest / lowest performing document types</div>
          {d.by_source_type?.length ? d.by_source_type.map((s) => (
            <div key={s.source_type} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0 text-sm">
              <span className="text-slate-700">{SOURCE_LABEL[s.source_type] || s.source_type} <span className="text-slate-400">({s.judged})</span></span>
              <span className="font-mono font-semibold text-slate-800">{fmtPct(s.agreement_rate)}</span>
            </div>
          )) : <div className="text-xs text-slate-400">No data yet.</div>}
        </Card>
      </div>
    </>
  );
};
